import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatTarget, InboxRow } from 'src/types/apps/messages';

interface MessagesState {
  unreadMessages: Record<string, number>;
  inbox: InboxRow[];
  signedIn: boolean;
  // The conversation open in the Chats screen's split view — kept in Redux
  // (not component state) so it survives navigating away from /pages/inbox/chats
  // and back, instead of resetting to the empty state on remount.
  activeChatTarget: ChatTarget | null;
}

const initialState: MessagesState = {
  unreadMessages: {},
  inbox: [],
  signedIn: false,
  activeChatTarget: null,
};

export const MessagesSlice = createSlice({
  name: 'Messages',
  initialState,
  reducers: {
    setUnreadMessages: (state, action: PayloadAction<Record<string, number>>) => {
      state.unreadMessages = action.payload;
    },
    clearRoomUnread: (state, action: PayloadAction<string>) => {
      // Delete the key (don't leave a 0) so the topbar sum matches
      // malaeb-player after opening a conversation — rooms with no unreads
      // simply disappear from the map.
      if (state.unreadMessages) {
        delete state.unreadMessages[action.payload];
      }
    },
    clearInboxRoomUnread: (state, action: PayloadAction<string>) => {
      // Optimistic inbox badge: `getRoomUnreadCount` is Math.max(live, inbox).
      // Live is cleared in `clearRoomUnread` immediately; inbox `unread_count`
      // would otherwise linger until the next `onValue` and keep the badge up.
      const roomId = action.payload;
      const row = state.inbox.find((item) => item.chatId === roomId);
      if (row) row.unreadCount = 0;
    },
    setInbox: (state, action: PayloadAction<InboxRow[]>) => {
      state.inbox = action.payload;
    },
    setSignedIn: (state, action: PayloadAction<boolean>) => {
      state.signedIn = action.payload;
    },
    setActiveChatTarget: (state, action: PayloadAction<ChatTarget | null>) => {
      state.activeChatTarget = action.payload;
    },
    reset: (state) => {
      state.unreadMessages = {};
      state.inbox = [];
      state.signedIn = false;
      state.activeChatTarget = null;
    },
  },
});

export const {
  setUnreadMessages,
  clearRoomUnread,
  clearInboxRoomUnread,
  setInbox,
  setSignedIn,
  setActiveChatTarget,
  reset,
} = MessagesSlice.actions;

export default MessagesSlice.reducer;
